import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  AtomicBlockUtils,
  ContentState,
  EditorState,
  Modifier,
  RichUtils,
  convertToRaw,
} from "draft-js";
import { Editor } from "react-draft-wysiwyg";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";
import styles from "./style/EventRichTextEditor.module.scss";
import { LuBold, LuHeading1, LuHeading2, LuIndentDecrease, LuIndentIncrease, LuRedo, LuStrikethrough, LuUndo } from "react-icons/lu";
import { MdOutlineColorLens } from "react-icons/md";
import { PiListBullets, PiListNumbers } from "react-icons/pi";

const COLOR_STYLE_PREFIX = "color-";
const LEGACY_TEXT_COLOR_PREFIX = "TEXT_COLOR_";
const MAX_LIST_DEPTH = 4;

const cssColorFromDraftColorSuffix = (suffix) => {
  const s = String(suffix ?? "").trim();
  if (!s) return "#000000";
  if (s.startsWith("#")) return s;
  if (/^(rgb|rgba|hsl|hsla)\(/i.test(s)) return s;
  if (/^[0-9a-fA-F]{3}$|^[0-9a-fA-F]{6}$|^[0-9a-fA-F]{8}$/.test(s)) {
    return `#${s}`;
  }
  return s;
};

const buildColorStyleMapFromContent = (contentState) => {
  const map = {};
  contentState.getBlockMap().forEach((block) => {
    if (!block) return;
    block.getCharacterList().forEach((charMeta) => {
      charMeta.getStyle().forEach((styleName) => {
        if (styleName.startsWith(COLOR_STYLE_PREFIX)) {
          const suffix = styleName.slice(COLOR_STYLE_PREFIX.length);
          map[styleName] = { color: cssColorFromDraftColorSuffix(suffix) };
          return;
        }
        if (styleName.startsWith(LEGACY_TEXT_COLOR_PREFIX)) {
          const hex = `#${styleName.slice(LEGACY_TEXT_COLOR_PREFIX.length)}`;
          map[styleName] = { color: hex };
        }
      });
    });
  });
  return map;
};
const PRESET_TEXT_COLORS = [
  "#333333",
  "#111827",
  "#6B7280",
  "#EF4444",
  "#F97316",
  "#EAB308",
  "#22C55E",
  "#06B6D4",
  "#3B82F6",
  "#8B5CF6",
  "#EC4899",
];

const EventRichTextEditor = ({ value, onChange }) => {
  const [editorState, setEditorState] = useState(() =>
    EditorState.createEmpty(),
  );
  const [textColor, setTextColor] = useState("#333333");
  const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);
  const [colorPopoverPos, setColorPopoverPos] = useState({ top: 0, left: 0 });
  const lastEmittedHtmlRef = useRef(null);
  const imageInputRef = useRef(null);
  const colorInputRef = useRef(null);
  const colorPickerWrapRef = useRef(null);
  const colorPopoverRef = useRef(null);

  useEffect(() => {
    const next = typeof value === "string" ? value : "";
    if (next === lastEmittedHtmlRef.current) {
      return;
    }
    lastEmittedHtmlRef.current = next;
    if (!next.trim()) {
      setEditorState(EditorState.createEmpty());
      return;
    }
    try {
      const converter = typeof htmlToDraft === "function" ? htmlToDraft : htmlToDraft.default;
      const contentBlock = converter(next);
      if (contentBlock) {
        const contentState = ContentState.createFromBlockArray(
          contentBlock.contentBlocks,
          contentBlock.entityMap,
        );
        setEditorState(EditorState.createWithContent(contentState));
      } else {
        setEditorState(EditorState.createEmpty());
      }
    } catch (err) {
      console.error("DraftJS conversion error:", err);
      setEditorState(EditorState.createEmpty());
    }
  }, [value]);

  useEffect(() => {
    if (!isColorPickerOpen) return undefined;
    const updatePopoverPos = () => {
      const anchorEl = colorPickerWrapRef.current;
      if (!anchorEl) return;
      const rect = anchorEl.getBoundingClientRect();
      setColorPopoverPos({
        top: rect.bottom + 8,
        left: rect.left,
      });
    };
    updatePopoverPos();

    const handleOutsideClick = (event) => {
      const inButton = colorPickerWrapRef.current?.contains(event.target);
      const inPopover = colorPopoverRef.current?.contains(event.target);
      if (!inButton && !inPopover) {
        setIsColorPickerOpen(false);
      }
    };

    const handleViewportChange = () => {
      updatePopoverPos();
    };

    document.addEventListener("mousedown", handleOutsideClick);
    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("scroll", handleViewportChange, true);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("scroll", handleViewportChange, true);
    };
  }, [isColorPickerOpen]);

  const handleEditorChange = useCallback(
    (state) => {
      setEditorState(state);
      const html = draftToHtml(convertToRaw(state.getCurrentContent()));
      lastEmittedHtmlRef.current = html;
      onChange(html);
    },
    [onChange],
  );

  const currentStyle = editorState.getCurrentInlineStyle();
  const currentBlockType = RichUtils.getCurrentBlockType(editorState);

  const customStyleMap = useMemo(
    () => buildColorStyleMapFromContent(editorState.getCurrentContent()),
    [editorState],
  );

  const withChange = (nextState) => {
    if (!nextState) return;
    handleEditorChange(nextState);
  };

  const toggleBlockType = (type) => {
    withChange(RichUtils.toggleBlockType(editorState, type));
  };

  const toggleInlineStyle = (styleName) => {
    withChange(RichUtils.toggleInlineStyle(editorState, styleName));
  };

  const adjustDepth = (amount) => {
    const selection = editorState.getSelection();
    const content = editorState.getCurrentContent();
    const nextContent = Modifier.adjustBlockDepth(
      content,
      selection,
      amount,
      MAX_LIST_DEPTH,
    );
    if (nextContent === content) return;
    const nextState = EditorState.push(
      editorState,
      nextContent,
      "adjust-depth",
    );
    withChange(nextState);
  };

  const applyTextColor = (hex) => {
    const raw = String(hex ?? "");
    const normalized = raw.startsWith("#")
      ? raw
      : `#${raw.replace(/^#/, "")}`;
    const withoutHash = normalized.slice(1).toUpperCase();
    const styleName = `${COLOR_STYLE_PREFIX}#${withoutHash}`;
    let nextState = editorState;
    currentStyle.forEach((s) => {
      if (
        s.startsWith(COLOR_STYLE_PREFIX) ||
        s.startsWith(LEGACY_TEXT_COLOR_PREFIX)
      ) {
        nextState = RichUtils.toggleInlineStyle(nextState, s);
      }
    });
    nextState = RichUtils.toggleInlineStyle(nextState, styleName);
    setTextColor(normalized);
    withChange(nextState);
  };

  const onSelectImageFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const src = reader.result;
      if (typeof src !== "string") return;
      const contentState = editorState.getCurrentContent();
      const contentStateWithEntity = contentState.createEntity(
        "IMAGE",
        "MUTABLE",
        { src },
      );
      const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
      const withEntity = EditorState.set(editorState, {
        currentContent: contentStateWithEntity,
      });
      const withImage = AtomicBlockUtils.insertAtomicBlock(
        withEntity,
        entityKey,
        " ",
      );
      withChange(withImage);
    };
    reader.readAsDataURL(file);
  };

  const blockRendererFn = (block) => {
    if (block.getType() !== "atomic") return null;
    return {
      component: (props) => {
        const entity = props.contentState.getEntity(props.block.getEntityAt(0));
        const data = entity.getData();
        if (!data?.src) return null;
        return (
          <img
            src={data.src}
            alt=""
            className={styles.insertedImage}
          />
        );
      },
      editable: false,
    };
  };

  return (
    <div className={styles.root}>
      <div className={styles.customToolbar}>
        <button type="button" className={`${styles.toolBtn} ${currentBlockType === "header-one" ? styles.toolBtnActive : ""}`} onClick={() => toggleBlockType("header-one")} aria-label="H1">
          <LuHeading1 size={18} />
        </button>
        <button type="button" className={`${styles.toolBtn} ${currentBlockType === "header-two" ? styles.toolBtnActive : ""}`} onClick={() => toggleBlockType("header-two")} aria-label="H2">
          <LuHeading2 size={18} />
        </button>
        <span className={styles.toolBtnSeparator} />
        <button type="button" className={`${styles.toolBtn} ${currentStyle.has("BOLD") ? styles.toolBtnActive : ""}`} onClick={() => toggleInlineStyle("BOLD")} aria-label="粗體">
          <LuBold size={18} />
        </button>
        <button type="button" className={`${styles.toolBtn} ${currentStyle.has("STRIKETHROUGH") ? styles.toolBtnActive : ""}`} onClick={() => toggleInlineStyle("STRIKETHROUGH")} aria-label="刪除線">
          <LuStrikethrough size={18} />
        </button>
        <div className={styles.colorPickerWrap} ref={colorPickerWrapRef}>
          <button
            type="button"
            className={`${styles.toolBtn} ${isColorPickerOpen ? styles.toolBtnActive : ""}`}
            onClick={() => setIsColorPickerOpen((prev) => !prev)}
            aria-label="文字顏色"
            aria-expanded={isColorPickerOpen}
          >
            <MdOutlineColorLens size={18} />
          </button>
        </div>
        <span className={styles.toolBtnSeparator} />
        <button type="button" className={`${styles.toolBtn} ${currentBlockType === "unordered-list-item" ? styles.toolBtnActive : ""}`} onClick={() => toggleBlockType("unordered-list-item")} aria-label="項目符號清單">
          <PiListBullets size={18} />
        </button>
        <button type="button" className={`${styles.toolBtn} ${currentBlockType === "ordered-list-item" ? styles.toolBtnActive : ""}`} onClick={() => toggleBlockType("ordered-list-item")} aria-label="編號清單">
          <PiListNumbers size={18} />
        </button>
        <button type="button" className={styles.toolBtn} onClick={() => adjustDepth(1)} aria-label="增加縮排">
          <LuIndentIncrease size={18} />
        </button>
        <button type="button" className={styles.toolBtn} onClick={() => adjustDepth(-1)} aria-label="減少縮排">
          <LuIndentDecrease size={18} />
        </button>
        <span className={styles.toolBtnSeparator} />
        {/* <button type="button" className={styles.toolBtn} onClick={() => imageInputRef.current?.click()} aria-label="插入圖片">
          <HiOutlinePhotograph size={18} />
        </button> */}
        <button type="button" className={styles.toolBtn} onClick={() => withChange(EditorState.undo(editorState))} aria-label="復原">
          <LuUndo size={18} />
        </button>
        <button type="button" className={styles.toolBtn} onClick={() => withChange(EditorState.redo(editorState))} aria-label="重做">
          <LuRedo size={18} />
        </button>
        <input ref={imageInputRef} type="file" accept="image/*" className={styles.hiddenInput} onChange={onSelectImageFile} />
      </div>
      <Editor
        editorState={editorState}
        onEditorStateChange={handleEditorChange}
        stripPastedStyles
        toolbarHidden
        editorClassName={styles.editorArea}
        wrapperClassName={styles.wrapper}
        customStyleMap={customStyleMap}
        blockRendererFn={blockRendererFn}
        placeholder="請輸入"
      />
      {typeof document !== "undefined" && isColorPickerOpen
        ? createPortal(
            <div
              ref={colorPopoverRef}
              className={styles.colorPopover}
              style={{
                top: `${colorPopoverPos.top}px`,
                left: `${colorPopoverPos.left}px`,
              }}
            >
              <div className={styles.colorSwatchGrid}>
                {PRESET_TEXT_COLORS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    className={styles.colorSwatch}
                    onClick={() => {
                      applyTextColor(color);
                      setIsColorPickerOpen(false);
                    }}
                    style={{ backgroundColor: color }}
                    aria-label={`選擇顏色 ${color}`}
                  />
                ))}
              </div>
              <div className={styles.colorPickerRow}>
                <span className={styles.colorPickerLabel}>自訂</span>
                <input
                  ref={colorInputRef}
                  type="color"
                  className={styles.colorInput}
                  value={textColor}
                  onChange={(e) => applyTextColor(e.target.value)}
                />
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
};

export default EventRichTextEditor;
